import { getEquipment, isAudioEquipment } from '../../utils/equipment';

export const inputKey = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase()
  .replace(/^hdmi\s*(\d+)$/, (_, port) => `hdmi ${Number(port)}`);
const identity = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const inputLabel = (input) => {
  if (input.canonicalPort) return input.canonicalPort;
  const port = String(input.label || '').match(/HDMI\s*(\d+)/i)?.[1];
  return port ? `HDMI ${Number(port)}` : String(input.label || input.id || '').trim().slice(0, 64);
};

export function buildInputInventory(screen, report) {
  const inputs = Array.isArray(report?.inputs) ? report.inputs : [];
  const byId = new Map(inputs.map((input) => [input.id, input]));
  const groups = new Map();
  const add = (label, physical = null) => {
    if (!label) return;
    const key = inputKey(label);
    const current = groups.get(key) || { input: label, physical: null, children: [], connections: [], disposition: 'unassigned' };
    if (physical) current.physical = physical;
    groups.set(key, current);
    return current;
  };
  inputs.filter((input) => !input.parentId && (input.passthrough || input.type === 'hdmi'))
    .forEach((input) => add(inputLabel(input), input));
  inputs.filter((input) => input.parentId).forEach((input) => {
    // Follow explicit parent relationships; never infer a port from a source's name.
    let parent = byId.get(input.parentId);
    const visited = new Set([input.id]);
    while (parent?.parentId && !visited.has(parent.id)) {
      visited.add(parent.id);
      parent = byId.get(parent.parentId);
    }
    if (!parent || visited.has(parent.id)) return;
    const group = groups.get(inputKey(inputLabel(parent)));
    if (group) group.children.push(input);
  });
  (screen?.activationStrategy?.inputInventory?.inputs || []).forEach((entry) => {
    const group = add(entry.input);
    if (group) group.disposition = entry.disposition;
  });
  (screen?.players || []).forEach((connection) => {
    const group = add(connection.expectedInput);
    if (group) group.connections.push(connection);
  });
  return [...groups.values()].map((group) => ({ ...group,
    disposition: group.connections.length
      ? group.connections.every((entry) => entry.isIgnored) ? 'ignored' : 'assigned'
      : group.disposition,
  })).sort((a, b) => a.input.localeCompare(b.input, undefined, { numeric: true }));
}

export function inputCandidates(group, devices) {
  return group.children.flatMap((child) => {
    const name = identity(child.label);
    if (!name || /^(hdmi|input|av|tv)( \d+)?$/.test(name)) return [];
    return devices.flatMap((device) => {
      const names = [device.name, ...(getEquipment(device)?.aliases || [])].map(identity).filter(Boolean);
      const exact = names.includes(name);
      const partial = names.some((alias) => alias.length >= 4 && name.length >= 4
        && (alias.includes(name) || name.includes(alias)));
      return exact || partial ? [{ device, child, confidence: exact ? 'high' : 'medium' }] : [];
    });
  });
}

export function setInputDisposition(screen, input, disposition) {
  const entries = screen.activationStrategy?.inputInventory?.inputs || [];
  return { ...screen,
    players: disposition === 'empty'
      ? (screen.players || []).filter((entry) => inputKey(entry.expectedInput) !== inputKey(input))
      : (screen.players || []).map((entry) => inputKey(entry.expectedInput) === inputKey(input)
        ? { ...entry, isIgnored: disposition === 'ignored' } : entry),
    activationStrategy: { ...screen.activationStrategy, inputInventory: { inputs: [
      ...entries.filter((entry) => inputKey(entry.input) !== inputKey(input)),
      { input, disposition },
    ] } },
  };
}

export function assignInputEquipment(screen, group, device, report, child = null) {
  const existing = (screen.players || []).find((entry) => Number(entry.deviceId || entry.id) === Number(device.id));
  const moved = existing && inputKey(existing.expectedInput) !== inputKey(group.input);
  const connection = {
    ...existing,
    deviceId: Number(device.id),
    name: device.name,
    expectedInput: group.input,
    sourceRole: isAudioEquipment(device) ? 'audio_system' : 'video_source',
    isIgnored: false,
    mappingMode: 'confirmed',
    mappingConfidence: 'high',
    mappingEvidence: {
      source: 'operator_input_assignment',
      reportSource: report?.source || null,
      reportCollectedAt: report?.collectedAt || null,
      inputId: child?.id || group.physical?.id || null,
      parentInputId: child?.parentId || null,
      reportedLabel: child?.label || group.physical?.label || null,
    },
    lastVerifiedAt: new Date().toISOString(),
    connectionConfig: { ...existing?.connectionConfig,
      ...(moved ? { activationOverride: null } : {}),
      ...(report?.connector && group.physical?.nativeInputId ? { inputRoute: {
        schemaVersion: 1, connector: report.connector, nativeInputId: group.physical.nativeInputId,
        canonicalPort: group.physical.canonicalPort, identityReference: report.identityReference,
        mappingEvidence: { source: 'operator_input_assignment', inputId: group.physical.nativeInputId },
        validation: !moved && existing?.connectionConfig?.inputRoute?.nativeInputId === group.physical.nativeInputId
          ? existing.connectionConfig.inputRoute.validation : { status: 'not_tested' },
      } } : moved ? { inputRoute: null } : {}),
    },
  };
  const next = setInputDisposition(screen, group.input, 'unassigned');
  const preferNative = report?.connector === 'lg_webos_remote' && group.physical?.nativeInputId
    && !screen.activationStrategy?.inputSwitching?.defaultMethod && !screen.activationStrategy?.inputSwitching?.methods?.length;
  if (preferNative) next.activationStrategy = { ...next.activationStrategy,
    inputSwitching: { defaultMethod: 'lg_webos', methods: ['lg_webos'] } };
  return { ...next, players: existing
    ? screen.players.map((entry) => Number(entry.deviceId || entry.id) === Number(device.id) ? connection : entry)
    : [...(screen.players || []), connection] };
}
